import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
    ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, Legend,
} from "recharts";
import { tooltipCardSx, TOOLTIP_MUTED } from "../theme/tooltipTheme";
import { DIRECTION_COLORS, directionKey, directionKind, directionLabel } from "./ModeContrastGroups";

const CANDIDATE_COLORS = { 1: "#93c5fd", 2: "#fdba74", both: "#6ee7b7" };
const OTHER_COLOR = "#cbd5e1";

const niceCeil = (value) => {
    const step = value > 50 ? 10 : 5;
    return Math.max(step, Math.ceil(value / step) * step);
};

const dot = (radius) => function Dot({ cx, cy, fill }) {
    if (cx == null || cy == null) return null;
    return <circle cx={cx} cy={cy} r={radius} fill={fill} />;
};
const OTHER_DOT = dot(1.6);
const CANDIDATE_DOT = dot(2.4);
const GROUPED_DOT = dot(4.2);

function PairTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
        <Box sx={{ ...tooltipCardSx, px: 1.25, py: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{point.series}</Typography>
            <Typography sx={{ fontSize: 12, color: TOOLTIP_MUTED }}>
                {`ΔE00 mode 1: ${point.x.toFixed(2)} · mode 2: ${point.y.toFixed(2)}`}
            </Typography>
        </Box>
    );
}

/** The shaded limit region of a pair category, clipped to the axes. */
function region(direction, xMax, yMax) {
    const kind = directionKind(direction);
    if (kind === "both") return { x1: 0, x2: Math.min(direction.max_mode_1, xMax), y1: 0, y2: Math.min(direction.max_mode_2, yMax) };
    if (direction.close_mode === 1) return { x1: 0, x2: Math.min(direction.max_close, xMax), y1: Math.min(direction.min_far, yMax), y2: yMax };
    return { x1: Math.min(direction.min_far, xMax), x2: xMax, y1: 0, y2: Math.min(direction.max_close, yMax) };
}

/** The ΔE00 extent a category's limits need on the axes to be visible. */
function limitExtent(d) {
    if (directionKind(d) === "both") return [d.max_mode_1 * 3, d.max_mode_2 * 3];
    if (d.close_mode === 1) return [d.max_close * 3, d.min_far * 1.25];
    return [d.min_far * 1.25, d.max_close * 3];
}

/**
 * Pair map of the Mode Contrast Filter: every pair of palette samples at its
 * ΔE00 in mode 1 (x) and in mode 2 (y), one pair category at a time. The
 * toggle above the map picks the category (a category that is switched off
 * is disabled); the map then shows that category's limits as a shaded
 * region, the pairs within them as small points and the selected pairs as
 * large points, over all other pairs in grey. The axes cover every pair
 * and every category's limits, so the scale does not change with the
 * toggle. The stable category is a property of single samples and has no
 * place here (it is in the result table and the groups). ``directions`` is
 * the API's per-category result (settings included); ``pairs`` its
 * ``pairs`` block (candidates and other pairs thinned server-side).
 */
export default function ModeContrastPairPlot({ pairs, directions, width = 640, height = 420 }) {
    const pairCategories = (directions || []).filter((d) => directionKind(d) !== "stable");
    const on = pairCategories.filter((d) => d.count > 0);
    const [picked, setPicked] = useState(on.length ? directionKey(on[0]) : null);
    // a category switched off while shown hands over to the first one still on
    useEffect(() => {
        if (!on.some((d) => directionKey(d) === picked)) setPicked(on.length ? directionKey(on[0]) : null);
    }, [on.map(directionKey).join(","), picked]);
    const shown = on.find((d) => directionKey(d) === picked) || null;

    // The axes: the same for every category, from all pairs and all limits.
    const { xMax, yMax } = useMemo(() => {
        const xs = [], ys = [];
        const take = (list) => (list || []).forEach((p) => { xs.push(Array.isArray(p) ? p[0] : p.x); ys.push(Array.isArray(p) ? p[1] : p.y); });
        take(pairs.other);
        Object.values(pairs.candidates || {}).forEach(take);
        Object.values(pairs.grouped || {}).forEach(take);
        for (const d of pairCategories) {
            const [lx, ly] = limitExtent(d);
            xs.push(lx); ys.push(ly);
        }
        return { xMax: niceCeil(Math.max(0, ...xs)), yMax: niceCeil(Math.max(0, ...ys)) };
    }, [pairs, pairCategories]);

    const { series, area } = useMemo(() => {
        const asPoints = (list, name) => (list || []).map(([x, y]) => ({ x, y, series: name }));
        const grouped = (key, name) => (pairs.grouped?.[key] || []).map((p) => ({ x: p.x, y: p.y, series: name }));
        // the other pairs: everything the shown category does not draw itself
        const others = [...asPoints(pairs.other, "Other pair")];
        for (const d of pairCategories) {
            const key = directionKey(d);
            if (shown && key === directionKey(shown)) continue;
            others.push(...asPoints(pairs.candidates?.[key], "Other pair"), ...grouped(key, "Other pair"));
        }
        const all = [{ key: "other", name: "Other pairs", color: OTHER_COLOR, shape: OTHER_DOT, data: others }];
        if (shown) {
            const key = directionKey(shown);
            const label = directionLabel(shown);
            all.push({
                key: `candidates-${key}`, name: "Within the limits", color: CANDIDATE_COLORS[key], shape: CANDIDATE_DOT,
                data: asPoints(pairs.candidates?.[key], `${label} · within the limits`),
            });
            all.push({
                key: `grouped-${key}`, name: `Selected pairs (${shown.groups.length})`, color: DIRECTION_COLORS[key], shape: GROUPED_DOT,
                data: grouped(key, `${label} · selected pair`),
            });
        }
        return {
            series: all.filter((s) => s.data.length > 0 || s.key.startsWith("grouped-")),
            area: shown ? { key: directionKey(shown), ...region(shown, xMax, yMax) } : null,
        };
    }, [pairs, pairCategories, shown, xMax, yMax]);

    const withinCount = shown ? pairs.candidate_counts?.[directionKey(shown)] ?? 0 : null;
    return (
        // The chart's SVG text inherits the app font from here (the browser default is serif).
        <Box
            sx={{ width: "100%", overflowX: "auto", fontFamily: (theme) => theme.typography.fontFamily }}
            data-testid="mode-contrast-pair-plot"
        >
            <ToggleButtonGroup
                exclusive size="small" value={picked} aria-label="Pair category shown"
                onChange={(_e, value) => { if (value) setPicked(value); }}
                sx={{ mb: 1.5, flexWrap: "wrap" }}
            >
                {pairCategories.map((d) => {
                    const key = directionKey(d);
                    return (
                        <ToggleButton key={key} value={key} disabled={d.count === 0} sx={{ textTransform: "none", px: 1.5 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: d.count === 0 ? "text.disabled" : DIRECTION_COLORS[key] }} />
                                <span>{d.count === 0 ? `${directionLabel(d)} · off` : directionLabel(d)}</span>
                            </Stack>
                        </ToggleButton>
                    );
                })}
            </ToggleButtonGroup>
            {!shown && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No pair category is switched on; the map shows the pairs only.
                </Typography>
            )}
            <ScatterChart width={width} height={height} margin={{ top: 8, right: 20, left: 12, bottom: 28 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    type="number" dataKey="x" domain={[0, xMax]} allowDataOverflow
                    label={{ value: "ΔE00 mode 1", position: "insideBottom", offset: -14 }}
                />
                <YAxis
                    type="number" dataKey="y" domain={[0, yMax]} allowDataOverflow
                    label={{ value: "ΔE00 mode 2", angle: -90, position: "insideLeft" }}
                />
                {area && (
                    <ReferenceArea
                        x1={area.x1} x2={area.x2} y1={area.y1} y2={area.y2}
                        fill={DIRECTION_COLORS[area.key]} fillOpacity={0.08}
                        stroke={DIRECTION_COLORS[area.key]} strokeOpacity={0.5}
                    />
                )}
                {series.map((s) => (
                    <Scatter
                        key={s.key} name={s.name} data={s.data} fill={s.color}
                        shape={s.shape} legendType="circle" isAnimationActive={false}
                    />
                ))}
                <Tooltip content={<PairTooltip />} cursor={false} isAnimationActive={false} />
                <Legend
                    verticalAlign="top" height={30} wrapperStyle={{ fontSize: 12 }}
                    // the marker carries the colour; light series colours are unreadable as text
                    formatter={(value) => <span style={{ color: "#475569" }}>{value}</span>}
                />
            </ScatterChart>
            <Typography variant="caption" color="text.secondary" display="block">
                {shown
                    ? `${pairs.total} pairs · ${withinCount} within the limits of "${directionLabel(shown)}" · ${shown.groups.length} of ${shown.count} pairs selected (${shown.selected} samples)`
                    : `${pairs.total} pairs`}
            </Typography>
        </Box>
    );
}
